<template>
  <b-form-group
    label="Tag this entry"
    label-class="label-title pb-0"
    description="You can add and customise your personal tags in the Profile section. If you're part of an organisation, you can also use organisation tags."
  >
    <b-form-tags
      id="tags-component-select"
      v-model="selectedOptions"
      class="mb-2"
      add-on-change
      no-outer-focus
    >
      <template
        v-slot="{ tags, inputAttrs, inputHandlers, disabled, removeTag }"
      >
        <ul v-if="tags.length > 0" class="list-inline d-inline-block mb-2">
          <li v-for="tag in tags" :key="tag" class="list-inline-item">
            <b-form-tag
              @remove="removeTag(tag)"
              :title="tag"
              :disabled="disabled"
              variant="info"
              >{{ tag }}</b-form-tag
            >
          </li>
        </ul>
        <SearchableDropdown
          :disabled="disabled || availableOptions.length === 0"
          :options="availableOptions"
          :group-by="'source'"
          placeholder="Search and select a tag..."
          @input="onTagSelected"
        />
      </template>
    </b-form-tags>
  </b-form-group>
</template>

<script>
import Providers from "@/providers";
import SearchableDropdown from "@/components/common/SearchableDropdown.vue";

export default {
  name: "Tags",
  components: {
    SearchableDropdown,
  },
  props: {
    value: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      selectedOptions: [],
      options: [],
    };
  },
  watch: {
    selectedOptions() {
      this.$emit("input", [...this.selectedOptions]);
    },
  },
  computed: {
    availableOptions() {
      return this.options.filter(
        (option) => this.selectedOptions.indexOf(option.value) === -1
      );
    },
  },
  methods: {
    onTagSelected(tagValue) {
      if (tagValue && !this.selectedOptions.includes(tagValue)) {
        this.selectedOptions.push(tagValue);
      }
    },
    async loadTagOptions() {
      let userTags = [];
      let organisationTags = [];

      try {
        userTags = await Providers.profile.tags();
      } catch (err) {
        console.warn("Failed to load user tags:", err);
      }

      try {
        organisationTags = await this.loadOrganisationTags();
      } catch (err) {
        console.warn("Failed to load organisation tags:", err);
      }

      const options = [];
      
      userTags.forEach(tag => {
        options.push({
          title: tag,
          value: tag,
          source: 'Personal Tags'
        });
      });
      
      organisationTags.forEach(tag => {
        if (!userTags.includes(tag)) {
          options.push({
            title: tag,
            value: tag,
            source: 'Organisation Tags'
          });
        }
      });
      
      this.options = options.sort((a, b) => a.title.localeCompare(b.title));
    },

    async loadOrganisationTags() {
      try {
        // Check if user is an organisation manager first
        const isOrgManager = this.$store.getters["authentication/isOrganisationManager"];
        
        // If user is an organisation manager, collect tags from ALL managed organisations
        if (isOrgManager) {
          try {
            const allTagsMap = await Providers.organisationManagement.getAllTags();
            
            const allTags = new Set();
            
            // The API returns an object { "123": ["Tag1", "Tag2"], "456": ["Tag3"] }
            // We need to flatten this into a single unique list
            if (allTagsMap && typeof allTagsMap === 'object') {
              Object.values(allTagsMap).forEach(tagsList => {
                if (Array.isArray(tagsList)) {
                  tagsList.forEach(tag => allTags.add(tag));
                }
              });
            }
            
            const mergedTags = Array.from(allTags);
            
            if (mergedTags.length > 0) {
              return mergedTags;
            }
          } catch (managerErr) {
            console.warn("Failed to load managed organisation tags:", managerErr);
          }
        }
        
        // Fallback: try to get organisation from personal profile
        const profileResp = await this.$api.v2.get("personal-profile");
        const profile = profileResp.body;

        if (profile && profile.organisation && profile.organisation.id) {
          const organisationTags =
            await Providers.organisationManagement.getTags(
              profile.organisation.id
            );
          return organisationTags || [];
        }

        return [];
      } catch (err) {
        console.warn("Organisation tags not available:", err);
        return [];
      }
    },
  },
  created() {
    if (this.value && Array.isArray(this.value) && this.value.length > 0) {
      this.selectedOptions = [...this.value];
    }
    this.loadTagOptions();
  },
  mounted() {
    // Sync tags again after mount in case they were loaded asynchronously
    if (this.value && Array.isArray(this.value) && this.value.length > 0) {
      this.selectedOptions = [...this.value];
    }
  }
};
</script>

<style scoped></style>
